import type { ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';

interface TooltipWrapperProps<T extends { disabled?: boolean }> {
  title: string;
  icon?: LucideIcon;
  children: ReactElement<T>;
  disabled?: boolean;
}

/**
 * TooltipWrapper
 * - Shows tooltip only if the wrapped element (like a Button) is disabled.
 * - Fully type-safe and visually enhanced.
 */
export function TooltipWrapper<T extends { disabled?: boolean }>({
  title,
  icon: Icon,
  disabled = false,
  children,
}: TooltipWrapperProps<T>) {
  if (disabled) {
    return children;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>

      <TooltipContent
        sideOffset={3}
        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-700 font-medium bg-white shadow-md animate-fadeIn"
      >
        {Icon && <Icon size={16} className="text-blue-400" />}
        <span>{title}</span>
      </TooltipContent>
    </Tooltip>
  );
}
